import React from "react";
import moment from "moment";
import { Label, Menu, MenuItem, Icon, Button, Popup } from "semantic-ui-react";
import { usePermissions } from "../../../hooks";
import "./CustomTable.scss";

export function CustomTable(props) {
  const {
    tableConfig,
    data,
    pagination,
    currentPage,
    setCurrentPage,
    onItemsPerPageChange,
    className = "",
    size = "default",
    bordered = true,
    hover = true,
    striped = false,
    loading = false,
  } = props;

  // Get permission checking function for RBAC button filtering
  const { hasPermission } = usePermissions();

  const getFieldValue = (row, field) => {
    if (
      typeof field === "string" ||
      (typeof field === "object" && field.field)
    ) {
      const fieldPath = typeof field === "string" ? field : field.field;
      if (fieldPath.includes("+")) {
        return fieldPath
          .split("+")
          .map((part) =>
            part
              .trim()
              .split(".")
              .reduce((acc, key) => acc && acc[key], row)
          )
          .join(" ");
      } else {
        return fieldPath.split(".").reduce((acc, key) => acc && acc[key], row);
      }
    }
    return null;
  };

  const getTableClasses = () => {
    let classes = "custom-table";

    if (className) classes += ` ${className}`;
    if (bordered) classes += " custom-table--bordered";
    if (hover) classes += " custom-table--hover";
    if (striped) classes += " custom-table--striped";

    // Adaptación según el tamaño
    switch (size) {
      case "tiny":
        classes += " custom-table--tiny";
        break;
      case "small":
        classes += " custom-table--small";
        break;
      case "large":
        classes += " custom-table--large";
        break;
      default:
        classes += " custom-table--default";
    }

    return classes;
  };

  const renderPageNumbers = (totalPages = null) => {
    const actualTotalPages = totalPages || getTotalPages();
    const pages = [];
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (actualTotalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = actualTotalPages;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(maxVisiblePages / 2);
      const maxPagesAfterCurrentPage = Math.ceil(maxVisiblePages / 2) - 1;

      if (currentPage <= maxPagesBeforeCurrentPage) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + maxPagesAfterCurrentPage >= actualTotalPages) {
        startPage = actualTotalPages - maxVisiblePages + 1;
        endPage = actualTotalPages;
      } else {
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    // Agregar primera página si no está visible
    if (startPage > 1) {
      pages.push(
        <MenuItem as="a" key={1} onClick={() => setCurrentPage(1)}>
          1
        </MenuItem>
      );

      if (startPage > 2) {
        pages.push(
          <MenuItem as="a" key="ellipsis-start" className="ellipsis" disabled>
            ...
          </MenuItem>
        );
      }
    }

    // Páginas visibles
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <MenuItem
          as="a"
          className={
            i === currentPage ? "custom-table__pagination-page__active" : ""
          }
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </MenuItem>
      );
    }

    // Agregar última página si no está visible
    if (endPage < actualTotalPages) {
      if (endPage < actualTotalPages - 1) {
        pages.push(
          <MenuItem as="a" key="ellipsis-end" className="ellipsis" disabled>
            ...
          </MenuItem>
        );
      }

      pages.push(
        <MenuItem
          as="a"
          key={actualTotalPages}
          onClick={() => setCurrentPage(actualTotalPages)}
        >
          {actualTotalPages}
        </MenuItem>
      );
    }

    return pages;
  };

  // Función para calcular la información de paginación
  const getPaginationInfo = () => {
    if (!pagination || !data) return "No hay datos disponibles";

    const itemsPerPage = pagination.limit || pagination.itemsPerPage || 10;
    const totalItems = pagination.total || pagination.totalItems || data.length;
    const totalPages =
      pagination.pages ||
      pagination.totalPages ||
      Math.ceil(totalItems / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return `Mostrando ${startItem}-${endItem} de ${totalItems} registros`;
  };

  // Función para obtener el número total de páginas de manera segura
  const getTotalPages = () => {
    if (!pagination) return 1;
    return (
      pagination.pages ||
      pagination.totalPages ||
      Math.ceil(
        (pagination.total || pagination.totalItems || data?.length || 0) /
          (pagination.limit || pagination.itemsPerPage || 10)
      )
    );
  };

  const renderCell = (row, field, rowIndex) => {
    const fieldValue = getFieldValue(row, field);
    const alignClass =
      field.align === "center"
        ? "text-center"
        : field.align === "right"
        ? "text-right"
        : "text-left";

    // Si el campo tiene una función render personalizada
    if (field.component === "custom" && field.render) {
      return <td className={alignClass}>{field.render(row, rowIndex)}</td>;
    } else if (field.buttons) {
      return (
        <td className={alignClass}>
          <div className="custom-table__actions">
            {field.buttons
              .filter((button) => {
                // If button has permission config, check if user has permission
                if (button.permission) {
                  const { module, action } = button.permission;
                  return hasPermission(module, action);
                }
                return true; // No permission config means always visible
              })
              .map((button, index) => {
                let isDisabled = false;
                if (button.disabled) {
                  const condition = button.disabled;
                  const func = new Function("row", `return ${condition};`);
                  isDisabled = func(row);
                }
                
                // Evaluar icon y color si son funciones
                const buttonIcon = typeof button.icon === 'function' 
                  ? button.icon(row) 
                  : button.icon;
                const buttonColor = typeof button.color === 'function' 
                  ? button.color(row) 
                  : button.color;
                
                return (
                  <Popup
                    key={index}
                    content={button.placeholder}
                    trigger={
                      <Button
                        color={buttonColor}
                        icon={buttonIcon}
                        onClick={() => button.onClick(row)}
                        disabled={isDisabled}
                      />
                    }
                  />
                );
              })}
          </div>
        </td>
      );
    } else if (field.dataType === "numeric") {
      const formattedValue = parseFloat(fieldValue).toFixed(field.decimal || 2);
      return (
        <td className={alignClass}>
          {field.money ? `S/. ${formattedValue}` : formattedValue}
        </td>
      );
    } else if (field.dataType === "date") {
      const formattedDate = fieldValue
        ? moment.utc(fieldValue).format(field.format || "DD/MM/YYYY")
        : "N/A";
      return <td className={alignClass}>{formattedDate}</td>;
    } else if (field.dataType === "text") {
      return <td className={alignClass}>{fieldValue}</td>;
    } else if (field.dataType === "text-bold") {
      return (
        <td className={alignClass}>
          <Label
            color={field.getColor(fieldValue)}
            style={{ fontWeight: "bold" }}
          >
            {field.getText ? field.getText(fieldValue) : fieldValue}
          </Label>
        </td>
      );
    } else if (field.component === "Label") {
      return (
        <td className={alignClass}>
          <Label color={field.getColor(fieldValue, row)}>
            {field.getText ? field.getText(fieldValue, row) : fieldValue}
          </Label>
        </td>
      );
    } else if (field.component === "CustomTable") {
      return (
        <td className={alignClass}>
          <CustomTable
            headers={field.headers}
            tableConfig={field.tableConfig}
            data={fieldValue}
            color={field.color}
          />
        </td>
      );
    } else {
      return <td className={alignClass}>{fieldValue}</td>;
    }
  };

  return (
    <div className={getTableClasses()}>
      <table>
        <thead>
          <tr>
            {tableConfig.headers.map((header, index) => (
              <th
                key={index}
                style={{
                  width: tableConfig.columnWidths
                    ? tableConfig.columnWidths[index]
                    : "auto",
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={tableConfig.headers.length}
                className="custom-table__no-data"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span className="ui active inline loader"></span>
                  Cargando...
                </div>
              </td>
            </tr>
          ) : data && data.length > 0 ? (
            data?.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {tableConfig.fields?.map((field, fieldIndex) => (
                  <React.Fragment key={fieldIndex}>
                    {renderCell(row, field, rowIndex)}
                  </React.Fragment>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={tableConfig.headers.length}
                className="custom-table__no-data"
              >
                No se encontraron resultados
              </td>
            </tr>
          )}
        </tbody>
        {pagination && data && data.length > 0 && !loading && (
          <tfoot>
            <tr>
              <td
                className="custom-table__pagination"
                colSpan={tableConfig.headers.length}
                style={{ padding: 0, border: "none" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem 1.5rem",
                    background: "var(--background-color, #f8f9fa)",
                    flexWrap: "wrap",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1.5rem",
                      fontSize: "0.9rem",
                      color: "var(--text-color, #6c757d)",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: "500", whiteSpace: "nowrap" }}>
                      {getPaginationInfo()}
                    </span>
                    {onItemsPerPageChange && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <label
                          style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}
                        >
                          Registros por página:
                        </label>
                        <select
                          style={{
                            padding: "0.3rem 0.6rem",
                            border:
                              "1px solid var(--background-color-40, #ced4da)",
                            borderRadius: "4px",
                            background: "var(--background-color, #ffffff)",
                            color: "var(--text-color, #495057)",
                            fontSize: "0.85rem",
                            cursor: "pointer",
                          }}
                          value={
                            pagination.limit || pagination.itemsPerPage || 10
                          }
                          onChange={(e) => {
                            const newLimit = parseInt(e.target.value);
                            if (onItemsPerPageChange) {
                              onItemsPerPageChange(newLimit);
                            }
                          }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <Menu floated="right" pagination>
                    <MenuItem
                      className="custom-table__pagination-page__first"
                      as="a"
                      icon
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                      title="Primera página"
                    >
                      <Icon name="angle double left" />
                    </MenuItem>
                    <MenuItem
                      className="custom-table__pagination-page__prev"
                      as="a"
                      icon
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      title="Página anterior"
                    >
                      <Icon name="angle left" />
                    </MenuItem>
                    {renderPageNumbers()}
                    <MenuItem
                      className="custom-table__pagination-page__next"
                      as="a"
                      icon
                      disabled={currentPage === getTotalPages()}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      title="Página siguiente"
                    >
                      <Icon name="angle right" />
                    </MenuItem>
                    <MenuItem
                      className="custom-table__pagination-page__last"
                      as="a"
                      icon
                      disabled={currentPage === getTotalPages()}
                      onClick={() => setCurrentPage(getTotalPages())}
                      title="Última página"
                    >
                      <Icon name="angle double right" />
                    </MenuItem>
                  </Menu>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

CustomTable.defaultProps = {
  size: "tiny",
};
